import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import api from '../services/api'

interface Colaborador {
  motcod: string
  motnome: string
  empresa_codigo: number | null
  empresa_nome: string | null
  dissidio_codigo: string | null
  dissidio_descricao: string | null
}

export default function NovaFichaPonto() {
  const navigate = useNavigate()
  const [busca, setBusca] = useState('')
  const [opcoes, setOpcoes] = useState<Colaborador[]>([])
  const [colaborador, setColaborador] = useState<Colaborador | null>(null)
  const [ano, setAno] = useState(new Date().getFullYear())
  const [mes, setMes] = useState(new Date().getMonth() + 1)
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [erro, setErro] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function buscarColaborador(texto: string) {
    setBusca(texto)
    setColaborador(null)
    if (texto.trim().length < 2) { setOpcoes([]); return }
    const r = await api.get('/cartao-ponto/colaboradores', { params: { busca: texto } })
    setOpcoes(r.data)
  }

  function selecionar(c: Colaborador) {
    setColaborador(c)
    setBusca(`${c.motcod} - ${c.motnome}`)
    setOpcoes([])
  }

  // O período (dias — de/até) é sugerido a partir do parâmetro cadastrado pra empresa
  // e dissídio do colaborador (dia_inicio_periodo) — recalcula sempre que o colaborador,
  // o ano ou o mês mudam. O digitador ainda pode ajustar as datas manualmente depois.
  useEffect(() => {
    if (!colaborador) return
    api.get('/cartao-ponto/periodo-sugerido', { params: { ano, mes, colaborador: colaborador.motcod } })
      .then(r => { setDataInicio(r.data.dataInicio); setDataFim(r.data.dataFim) })
  }, [colaborador, ano, mes])

  async function salvar() {
    if (!colaborador) { setErro('Selecione um colaborador.'); return }
    if (!colaborador.dissidio_codigo) {
      setErro('Este colaborador não tem dissídio cadastrado — cadastre em Funcionários > Configurações > Cadastro de Colaboradores antes de abrir a ficha.')
      return
    }
    if (!dataInicio || !dataFim) { setErro('Informe o período de dias.'); return }
    setSalvando(true)
    setErro('')
    try {
      await api.post('/cartao-ponto/fichas', {
        ano, mes, colaborador: colaborador.motcod, dataInicio, dataFim,
      })
      navigate(`/funcionarios/ficha-ponto/${ano}/${mes}/${colaborador.motcod}`)
    } catch (e: any) {
      setErro(e.response?.data?.error ?? 'Erro ao criar ficha.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="card">
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          Nova Ficha Ponto
          <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }} title="Tabela frota.cartta">CARTTA</span>
        </h3>

        <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <div className="field" style={{ gridColumn: '1 / -1', position: 'relative' }}>
            <label>Colaborador (código ou nome) *</label>
            <input
              type="text"
              value={busca}
              onChange={e => buscarColaborador(e.target.value)}
              placeholder="Digite o código ou o nome"
            />
            {opcoes.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
                maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}>
                {opcoes.map(c => (
                  <div
                    key={c.motcod}
                    onClick={() => selecionar(c)}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '0.85rem' }}
                    onMouseDown={e => e.preventDefault()}
                  >
                    <b>{c.motcod}</b> — {c.motnome}
                  </div>
                ))}
              </div>
            )}
          </div>

          {colaborador && (
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Empresa e dissídio (do cadastro do colaborador)</label>
              <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                <div>{colaborador.empresa_codigo} - {colaborador.empresa_nome}</div>
                {colaborador.dissidio_codigo ? (
                  <div>{colaborador.dissidio_codigo} - {colaborador.dissidio_descricao}</div>
                ) : (
                  <div style={{ color: '#b45309' }}>
                    Sem dissídio cadastrado —{' '}
                    <Link to="/funcionarios/configuracoes/colaboradores">cadastre aqui</Link> antes de abrir a ficha.
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="field">
            <label>Ano (período) *</label>
            <input type="number" value={ano} onChange={e => setAno(Number(e.target.value))} />
          </div>

          <div className="field">
            <label>Mês (período) *</label>
            <select value={mes} onChange={e => setMes(Number(e.target.value))}>
              {['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
                .map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>

          <div className="field">
            <label>Dias — de *</label>
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
          </div>

          <div className="field">
            <label>Dias — até *</label>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
          </div>
        </div>

        {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.5rem' }}>{erro}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
          <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
            {salvando ? 'Criando...' : 'Criar Ficha'}
          </button>
          <button className="btn btn-outline" onClick={() => navigate('/funcionarios/ficha-ponto')}>Cancelar</button>
        </div>
      </div>
    </div>
  )
}
